import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Save, Send, Loader as Loader2, ToggleLeft, ToggleRight, CircleCheck as CheckCircle, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useIssueEmailSchedule } from '../../hooks/useIssueEmailSchedule';

const DAY_LABELS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

export const IssueEmailScheduler: React.FC = () => {
  const {
    schedule,
    contacts,
    loading,
    saving,
    sendingTest,
    error,
    saveSchedule,
    sendTestEmail,
    DAYS,
  } = useIssueEmailSchedule();

  const [isEnabled, setIsEnabled] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>(['monday', 'wednesday', 'friday']);
  const [executionTime, setExecutionTime] = useState('09:00');
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (schedule) {
      setIsEnabled(schedule.is_enabled);
      setSelectedDays(schedule.schedule_days || []);
      setExecutionTime(schedule.execution_time?.slice(0, 5) || '09:00');
      setSelectedRecipients(schedule.recipient_ids || []);
    }
  }, [schedule]);

  const toggleDay = (day: string) => {
    setDirty(true);
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleRecipient = (id: string) => {
    setDirty(true);
    setSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (selectedRecipients.length === 0 && isEnabled) {
      setFeedback({ type: 'error', message: 'Select at least one recipient before enabling the schedule.' });
      return;
    }
    if (selectedDays.length === 0 && isEnabled) {
      setFeedback({ type: 'error', message: 'Select at least one day before enabling the schedule.' });
      return;
    }

    try {
      await saveSchedule({
        is_enabled: isEnabled,
        schedule_days: selectedDays,
        execution_time: executionTime,
        recipient_ids: selectedRecipients,
      });
      setDirty(false);
      setFeedback({ type: 'success', message: 'Schedule saved successfully.' });
      setTimeout(() => setFeedback(null), 4000);
    } catch {
      setFeedback({ type: 'error', message: 'Failed to save schedule.' });
    }
  };

  const handleTestEmail = async () => {
    if (selectedRecipients.length === 0) {
      setFeedback({ type: 'error', message: 'Select at least one recipient to send a test email.' });
      return;
    }
    try {
      await sendTestEmail(selectedRecipients);
      setFeedback({ type: 'success', message: 'Test email sent! Check the inbox of selected recipients.' });
      setTimeout(() => setFeedback(null), 5000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setFeedback({ type: 'error', message: `Failed to send test email: ${msg}` });
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-5 border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Email Schedule
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Automatically send issue reports on selected days
              </p>
            </div>
          </div>
          <button
            onClick={() => { setIsEnabled(!isEnabled); setDirty(true); }}
            className="flex items-center gap-2"
          >
            {isEnabled ? (
              <ToggleRight className="h-8 w-8 text-amber-500" />
            ) : (
              <ToggleLeft className="h-8 w-8 text-gray-400" />
            )}
            <span className={`text-sm font-medium ${isEnabled ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'}`}>
              {isEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </button>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {feedback && (
          <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
            feedback.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40'
          }`}>
            {feedback.type === 'success' ? (
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
            )}
            {feedback.message}
          </div>
        )}

        {error && !feedback && (
          <div className="flex items-center gap-2 p-3 rounded-xl text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            <Calendar className="h-4 w-4" />
            Schedule Days
          </label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((day) => {
              const active = selectedDays.includes(day);
              return (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/20'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {DAY_LABELS[day]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            <Clock className="h-4 w-4" />
            Execution Time (UTC)
          </label>
          <input
            type="time"
            value={executionTime}
            onChange={(e) => { setExecutionTime(e.target.value); setDirty(true); }}
            className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            <Users className="h-4 w-4" />
            Recipients
          </label>
          {contacts.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
              No worker contacts found. Add contacts in Settings &gt; Workers Function &amp; Emails.
            </p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {contacts.map((c) => {
                const checked = selectedRecipients.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      checked
                        ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRecipient(c.id)}
                      className="h-4 w-4 text-amber-500 rounded border-gray-300 dark:border-gray-600 focus:ring-amber-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {c.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {c.email} {c.job_function ? `- ${c.job_function}` : ''}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {schedule?.last_executed_at && (
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
            Last executed: {new Date(schedule.last_executed_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between p-5 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/30">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleTestEmail}
          disabled={sendingTest || selectedRecipients.length === 0}
          loading={sendingTest}
        >
          <Send className="h-4 w-4 mr-1.5" />
          Send Test Email
        </Button>

        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !dirty}
          loading={saving}
        >
          <Save className="h-4 w-4 mr-1.5" />
          Save Schedule
        </Button>
      </div>
    </div>
  );
};
